import React, { useCallback, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { apiService, PlaidExchangeTokenRequest } from '../services/api';
import { Button } from './ui/button';

interface PlaidLinkButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
}

export const PlaidLinkButton: React.FC<PlaidLinkButtonProps> = ({
  onSuccess,
  onError,
  variant = 'default',
  size = 'default',
  className,
  children = 'Connect Bank Account',
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch link token when component mounts or when button is clicked
  const fetchLinkToken = async () => {
    try {
      setLoading(true);
      const response = await apiService.createPlaidLinkToken();
      setLinkToken(response.link_token);
    } catch (error) {
      console.error('Error fetching link token:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Failed to initialize Plaid Link');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle successful Plaid Link flow
  const handleOnSuccess = useCallback(
    async (public_token: string, metadata: any) => {
      try {
        // Extract institution information
        const institutionId = metadata.institution?.institution_id || '';
        const institutionName = metadata.institution?.name || 'Unknown Institution';
        const selectedAccountIds: string[] =
          (metadata.accounts || [])
            .map((a: any) => a?.id || a?.account_id)
            .filter(Boolean);

        // Exchange public token for access token
        const exchangeData: PlaidExchangeTokenRequest = {
          public_token,
          institution_id: institutionId,
          institution_name: institutionName,
          selected_account_ids: selectedAccountIds.length ? selectedAccountIds : undefined,
        };

        await apiService.exchangePlaidToken(exchangeData);

        // Notify parent component of success
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error exchanging token:', error);
        if (onError) {
          onError(error instanceof Error ? error.message : 'Failed to connect bank account');
        }
      }
    },
    [onSuccess, onError]
  );

  // Handle Plaid Link exit
  const handleOnExit = useCallback(
    (error: any, metadata: any) => {
      if (error) {
        console.error('Plaid Link exit with error:', error);
        if (onError) {
          onError(error.error_message || 'Failed to connect bank account');
        }
      }
    },
    [onError]
  );

  // Initialize Plaid Link
  const config = {
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  };

  const { open, ready } = usePlaidLink(config);

  // Handle button click
  const handleClick = async () => {
    if (!linkToken) {
      await fetchLinkToken();
      // The useEffect below will automatically open when ready
    } else if (ready) {
      open();
    }
  };

  // Open Plaid Link when ready and token is available
  React.useEffect(() => {
    if (ready && linkToken) {
      // Auto-open when ready after fetching the token
      open();
    }
  }, [ready, linkToken, open]);

  return (
    <Button
      onClick={handleClick}
      disabled={loading || (!ready && linkToken !== null)}
      variant={variant}
      size={size}
      className={className}
    >
      {loading ? 'Loading...' : children}
    </Button>
  );
};

